import { useDepartmentText } from 'model/DepartmentTerminology';
import { useForm } from '@mantine/form';
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Group,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { randomId } from '@mantine/hooks';
import Button from 'components/Button';
import FormInputGroup from 'components/forms/FormInputGroup';
import { noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconArrowUpRight,
  IconCalendar,
  IconCheck,
  IconEye,
  IconPlus,
  IconTrashX,
  IconFile,
} from '@tabler/icons-react';
import createValidator from 'components/forms/validators/create-validator';
import ActionIcon from 'components/ActionIcon';
import required from 'components/forms/validators/rules/rule-required';
import H3Medium from 'components/typography/H3Medium';
import P2Medium from 'components/typography/P2Medium';
import { DEVICE_FAULTS_PAGE_PATH, EDIT_DEVICE_PAGE_PATH, REDIRECT_PAGE_PATH } from 'routes/paths';
import { DepartmentSelect, DepartmentSelectItem } from 'components/selects/DepartmentSelect';
import FormWrapper from 'components/forms/FormWrapper';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { DeviceSubtypeSelect } from 'components/selects/DeviceSubtypeSelect';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FormEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { DISCONTINUED_FAULT_SEVERITY_IDS, FaultSeveritySelect } from 'components/selects/FaultSeveritySelect';
import P1Medium from 'components/typography/P1Medium';
import P3Regular from 'components/typography/P3Regular';
import P2Regular from 'components/typography/P2Regular';
import LinkWithConfirm from 'components/modals/confirm/LinkWithConfirm';
import Toast from 'components/Toast';
import DeviceFiles from 'components/forms/device/DeviceFiles';
import { addMonths } from 'date-fns';
import { DeviceResponsiblePersonSelect } from 'components/selects/DeviceResponsiblePersonSelect';
import { DateInput, stringifyDate } from 'components/inputs/DateInput';
import { useConfirm } from 'components/modals/message/MessageProvider';
import RevisionPeriodSelect from 'components/inputs/revision-period-select/RevisionPeriodSelect';
import { showNotification } from '@mantine/notifications';
import { ERROR_NOTIFICATION_COLOR } from 'utils/constants';
import {
  FEATURE_TOGGLE_DEVICE_QR_CODES,
  FEATURE_TOGGLE_DEVICE_SUBTYPE_EVIDENCE_FIELDS,
  FEATURE_TOGGLE_PARENT_DEVICE,
} from 'env';
import { DeviceFileType } from 'model/DeviceFileType';
import DeviceEvidenceField from 'components/forms/device/DeviceEvidenceField';
import useDeviceEvidenceFields from 'components/forms/device/use-device-evidence-fields';
import EvidenceSections from 'components/forms/evidence-field/EvidenceSections';
import useEvidenceSections from 'components/forms/evidence-field/use-evidence-sections';
import { DeviceEvidenceFieldType } from 'components/forms/device/DeviceEvidenceField';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { Radio } from '@mantine/core';
import { DeviceSelect } from 'components/selects/DeviceSelect';
import P3Medium from 'components/typography/P3Medium';
import ConfirmDeviceDepartmentChangeModal from 'components/modals/confirm-device-department-change/ConfirmDeviceDepartmentChangeModal';
import ChildDeviceNameText from 'components/forms/device/ChildDeviceNameText';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import ParentDeviceRevisionPlans from 'components/forms/device/ParentDeviceRevisionPlans';
import ParentDeviceFaults from 'components/forms/device/ParentDeviceFaults';
import ParentDeviceFiles from 'components/forms/device/ParentDeviceFiles';
import PredefinedDataAutosuggestInput from 'components/inputs/predefined-data-autosuggest-input/PredefinedDataAutosuggestInput';
import { getDeviceFormReferrer } from 'components/forms/device/device-form-referrer';
import DeviceQrIdInput from 'components/inputs/DeviceQrIdInput';
import deviceQrId from 'components/forms/validators/rules/rule-device-qr-id';

export interface DeviceFile {
  deviceFileId?: number;
  fileId: string;
  fileName: string;
  originalFileName: string;
  uploadedAt: Date;
  key: string;
  deleted: boolean;
  isMainImage?: boolean;
  hasKepSignature?: boolean;
  type: DeviceFileType;
}

/**
 * The data collected from the form.
 */
export interface DeviceFormData {
  deviceName: string;
  manufacturer?: string;
  serialNumber?: string;
  qrId?: string;
  manufactured?: number;
  internalPossessionsNumber?: string;
  internalNote?: string;
  structure?: string;
  building?: string;
  floor?: string;
  room?: string;
  latitude?: number;
  longitude?: number;
  deviceTypeId: string;
  deviceSubtypeId: string;
  departmentId: string;
  responsiblePersonId?: string;
  responsiblePersonNote?: string;
  externalId?: string;
  nextRevision: {
    revisionPlanId?: number;
    description: string;
    date: string;
    revisionPeriod: string;
    key: string;
    externalId?: string;
    deleted: boolean;
    nextDate?: string;
    neverExecuted: boolean;
    deviceTypeId?: string;
  }[];
  faults: {
    faultId?: number;
    faultName: string;
    faultSeverityId?: string;
    description?: string;
    key: string;
    fixed?: {
      fixedAt?: string;
      fixedNote?: string;
      fixedBy?: { fullName?: string; firstName: string; lastName: string };
      recordedBy?: { fullName?: string; firstName: string; lastName: string };
    };
    deleted: boolean;
  }[];
  files: DeviceFile[];
  evidenceFields: { [evidenceFieldId: string]: DeviceEvidenceFieldType };
  parentDeviceId?: string;
  childDeviceIds?: string[];
}

/**
 * The default initial values.
 */
export const deviceFormInitialValues: DeviceFormData = {
  deviceName: '',
  manufacturer: '',
  serialNumber: '',
  qrId: '',
  internalPossessionsNumber: '',
  internalNote: '',
  structure: 'simple',
  building: '',
  floor: '',
  room: '',
  deviceTypeId: '',
  deviceSubtypeId: '',
  departmentId: '',
  responsiblePersonId: '',
  responsiblePersonNote: '',
  externalId: '',
  nextRevision: [
    { description: '', date: '', revisionPeriod: '', key: randomId(), deleted: false, neverExecuted: false },
  ],
  faults: [],
  files: [],
  evidenceFields: {},
  parentDeviceId: '',
  childDeviceIds: [],
};

/**
 * Parameters of the DeviceForm.;
 */
export interface DeviceFormProps extends FormProps<DeviceFormData> {
  readOnly?: Partial<Record<keyof DeviceFormData, boolean>>;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
  hidePrimaryButton?: boolean;
  hideSecondaryButton?: boolean;
  hideFooter?: boolean;
  remoteSource?: { remoteSourceId: number; name: string; type: string };
  isAddMode?: boolean;
  departmentOrganizationId?: number;
  initialDepartment?: Pick<DepartmentSelectItem, 'departmentId' | 'breadcrumbs'>;
  confirmDepartmentChange?: boolean;
  showQrId?: boolean;
}

/**
 * Form for creating and editing device.
 */
export default function DeviceForm({
  initialValues = deviceFormInitialValues,
  onSubmit = noop,
  readOnly = {},
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
  hidePrimaryButton = false,
  hideSecondaryButton = false,
  hideFooter = false,
  remoteSource,
  isAddMode = false,
  departmentOrganizationId,
  initialDepartment,
  confirmDepartmentChange = false,
  showQrId = true,
}: DeviceFormProps) {
  const departmentText = useDepartmentText();
  const deadlinesAnchorRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { isMobile, isTablet } = useResponsiveDesign();
  const { confirm } = useConfirm();
  const [openedToast, setOpenedToast] = useState(true);
  const [departmentChangeModalOpened, setDepartmentChangeModalOpened] = useState(false);
  const [newChildDeviceId, setNewChildDeviceId] = useState('');
  const [hasVisibleFileTabs, setHasVisibleFileTabs] = useState(true);
  const [departments, setDepartments] = useState<DepartmentSelectItem[]>([]);

  const { deviceId } = useParams();
  const theme = useMantineTheme();

  const excludeDeviceIds = useMemo(() => (deviceId ? [Number(deviceId)] : []), [deviceId]);
  const deviceFormReferrer = useMemo(() => getDeviceFormReferrer(location.state), [location.state]);
  const effectivePrimaryButtonText = deviceFormReferrer
    ? deviceFormReferrer.kind === 'revision'
      ? 'Uložiť a pokračovať do RS'
      : 'Uložiť a pokračovať do formulára'
    : primaryButtonText;
  const effectiveSecondaryButtonText = deviceFormReferrer
    ? deviceFormReferrer.kind === 'revision'
      ? 'Vrátiť sa do RS bez uloženia zmien'
      : 'Vrátiť sa do formulára bez uloženia zmien'
    : undefined;
  const effectiveCloseConfirmTitle = deviceFormReferrer
    ? deviceFormReferrer.kind === 'revision'
      ? 'Návrat do RS bez uloženia zmien'
      : 'Návrat do formulára bez uloženia zmien'
    : undefined;
  const effectiveCloseConfirmMessage = deviceFormReferrer
    ? deviceFormReferrer.kind === 'revision'
      ? 'Naozaj sa chcete vrátiť do RS bez uloženia zmien? Všetky neuložené zmeny budú stratené.'
      : 'Naozaj sa chcete vrátiť do formulára bez uloženia zmien? Všetky neuložené zmeny budú stratené.'
    : undefined;
  const effectivePrimaryIcon = deviceFormReferrer ? (
    <IconArrowRight stroke="1.5" height={24} width={24} />
  ) : (
    primaryIcon
  );

  const form = useForm<DeviceFormData>({
    initialValues,
    validate: {
      deviceName: createValidator([required]),
      deviceTypeId: createValidator([required]),
      deviceSubtypeId: createValidator([required]),
      departmentId: createValidator([required]),
      qrId: createValidator([deviceQrId]),
      faults: {
        faultName: createValidator([required]),
      },
    },
  });

  const selectedDepartment =
    departments.find(({ departmentId }) => String(departmentId) === form.values.departmentId) ??
    (String(initialDepartment?.departmentId) === form.values.departmentId ? initialDepartment : undefined);
  const showLegacyLocationFields =
    !form.values.departmentId || selectedDepartment?.breadcrumbs[0]?.hierarchyLevelSlug === 'department.departmentId';

  const addChildDeviceExcludeDeviceIds = useMemo(
    () => [
      ...excludeDeviceIds,
      ...(form.values.childDeviceIds ?? []).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
    ],
    [excludeDeviceIds, form.values.childDeviceIds?.join(',')]
  );

  const highlightedRevisionPlanId = useMemo(() => {
    const revisionPlanIdParam = searchParams.get('revisionPlanId');

    if (!revisionPlanIdParam) {
      return undefined;
    }

    const revisionPlanId = Number(revisionPlanIdParam);
    return Number.isNaN(revisionPlanId) ? undefined : revisionPlanId;
  }, [searchParams]);
  const [isRevisionPlanHighlightVisible, setIsRevisionPlanHighlightVisible] = useState(
    Boolean(searchParams.get('revisionPlanId'))
  );

  useDirtyFormHandler(form);

  const showRemoteSource = Boolean(remoteSource) && !isAddMode;

  /**
   * Opens confirm modal which confirmation deletes the next revision.
   */
  function handleDeleteNextRevision(index: number) {
    confirm({
      title: 'Potvrdenie zmazania',
      content: 'Naozaj si prajete vymazať sledovaný termín?',
      onConfirm: () => {
        form.setFieldValue(`nextRevision.${index}.deleted`, true);
      },
    });
  }

  /**
   * Opens confirm modal which confirmation deletes the device fault.
   */
  function handleDeleteFault(index: number) {
    confirm({
      title: 'Potvrdenie zmazania',
      content: 'Naozaj si prajete vymazať nedostatok zariadenia?',
      onConfirm: () => {
        // mark faults as deleted only if they have faultId
        // otherwise, delete them - they were not saved yet
        if (form.values.faults[index].faultId) {
          form.setFieldValue(`faults.${index}.deleted`, true);
        } else {
          form.removeListItem('faults', index);
        }
      },
    });
  }

  /**
   * Opens confirm modal before removing a child device association.
   */
  function handleRemoveChildDevice(index: number) {
    confirm({
      title: 'Odobrať podradené zariadenie',
      content: 'Naozaj si prajete odobrať podradené zariadenie?',
      onConfirm: () => {
        const childDeviceIds = form.values.childDeviceIds?.filter((_, i) => i !== index) ?? [];
        form.setFieldValue('childDeviceIds', childDeviceIds);
      },
    });
  }

  const parentDeviceLink = `${REDIRECT_PAGE_PATH.original}?to=${encodeURIComponent(
    EDIT_DEVICE_PAGE_PATH.insert({ deviceId: form.values.parentDeviceId })
  )}`;

  // COMPONENT : Device faults
  const deviceFaultsFields = useMemo(() => {
    return form.values.faults.map((item, index) => {
      if (item.deleted) {
        return null;
      }

      const isFixed = item.fixed?.fixedAt !== undefined;

      return (
        <Stack
          key={item.key}
          pt={16}
          pb={32}
          px={isTablet ? 20 : 40}
          ml={isTablet ? 20 : 40}
          mr={24}
          mb={24}
          spacing={24}
          bg="gray.0"
          sx={() => ({
            borderRadius: '4px',
          })}
        >
          <Group
            pb={16}
            position="apart"
            align={isMobile ? 'stretch' : 'center'}
            noWrap={!isMobile}
            style={{
              flexDirection: isMobile ? 'column' : undefined,
              borderBottom: `1px solid ${theme.colors.gray[2]}`,
            }}
          >
            {isFixed ? (
              <Group spacing={8}>
                <IconCheck stroke="1.5" height={24} width={24} />
                <P1Medium>Vyriešený nedostatok</P1Medium>
              </Group>
            ) : (
              <Group spacing={8}>
                <IconAlertTriangle stroke="1.5" height={24} width={24} />
                <P1Medium>Nevyriešený nedostatok</P1Medium>
              </Group>
            )}
            <Button
              variant="danger-secondary"
              leftIcon={<IconTrashX stroke="1.5" height={24} width={24} color={theme.colors.red[8]} />}
              type="button"
              size="md"
              loading={loading}
              onClick={() => handleDeleteFault(index)}
              fullWidth={isMobile}
            >
              Zmazať
            </Button>
          </Group>
          <Stack spacing={24}>
            <Grid align="center" justify="flex-start" grow gutter={40}>
              <Grid.Col span={isMobile ? 12 : 6}>
                <PredefinedDataAutosuggestInput.ByTypeName
                  label="Názov"
                  placeholder="--"
                  size="lg"
                  withAsterisk
                  name={`objekto-faults.${index}.faultName`}
                  {...form.getInputProps(`faults.${index}.faultName`)}
                  readOnly={isFixed}
                  deviceTypeId={form.values.deviceTypeId ? Number(form.values.deviceTypeId) : undefined}
                  predefinedDataTypeName="Nedostatky"
                />
              </Grid.Col>

              <Grid.Col span={isMobile ? 12 : 6}>
                <FaultSeveritySelect
                  label="Závažnosť"
                  size="lg"
                  placeholder="Vyberte závažnosť"
                  searchable
                  clearable
                  excludedFaultSeverityIds={[...DISCONTINUED_FAULT_SEVERITY_IDS]}
                  keepExcludedValueVisible
                  name={`objekto-faults.${index}.faultSeverityId`}
                  {...form.getInputProps(`faults.${index}.faultSeverityId`)}
                  readOnly={isFixed}
                />
              </Grid.Col>
            </Grid>
            <SimpleGrid cols={1}>
              <TextInput
                label="Popis"
                placeholder="--"
                size="lg"
                name={`objekto-faults.${index}.description`}
                {...form.getInputProps(`faults.${index}.description`)}
                readOnly={isFixed}
              />
            </SimpleGrid>
          </Stack>
          {isFixed && (
            <Box
              py={16}
              pb={24}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 174px',
                gap: '24px',
                alignItems: 'center',
              }}
            >
              <Stack>
                <P3Regular color="gray.6">Dátum riešenia nedostatku</P3Regular>
                {/* If isFixed === true, the fault has fixedAt prop  */}
                <P2Regular color="gray.8">{new Date(item.fixed?.fixedAt!).toLocaleDateString()}</P2Regular>
              </Stack>
              <Stack>
                <P3Regular color="gray.6">Vyriešil</P3Regular>
                <P2Regular color="gray.8">
                  {item.fixed?.fixedBy?.fullName ??
                    (item.fixed?.fixedBy ? `${item.fixed.fixedBy.firstName} ${item.fixed.fixedBy.lastName}` : '')}
                </P2Regular>
              </Stack>
              <Stack>
                <P3Regular color="gray.6">Zaznamenal riešenie</P3Regular>
                <P2Regular color="gray.8">
                  {(item.fixed?.recordedBy ?? item.fixed?.fixedBy)?.fullName ??
                    (item.fixed?.fixedBy
                      ? `${(item.fixed.recordedBy ?? item.fixed.fixedBy).firstName} ${
                          (item.fixed.recordedBy ?? item.fixed.fixedBy).lastName
                        }`
                      : '')}
                </P2Regular>
              </Stack>
              <Stack>
                <P3Regular color="gray.6">Popis riešenia</P3Regular>
                <P2Regular color="gray.8">{item.fixed?.fixedNote}</P2Regular>
              </Stack>
            </Box>
          )}
        </Stack>
      );
    });
  }, [form, theme, isMobile, isTablet, loading]);

  useEffect(() => {
    if (searchParams.get('revisionPlanId') && deadlinesAnchorRef.current) {
      deadlinesAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (highlightedRevisionPlanId === undefined) {
      setIsRevisionPlanHighlightVisible(false);
      return;
    }

    setIsRevisionPlanHighlightVisible(true);
    const timeoutId = window.setTimeout(() => {
      setIsRevisionPlanHighlightVisible(false);
    }, 10000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [highlightedRevisionPlanId]);

  useEffect(() => {
    if (form.values.deviceTypeId) {
      form.values.nextRevision.forEach((item, index) => {
        if (!item.deviceTypeId) {
          form.setFieldValue(`nextRevision.${index}.deviceTypeId`, form.values.deviceTypeId);
        }
      });
    }
  }, [form.values.deviceTypeId]);

  useEffect(() => {
    setNewChildDeviceId('');
  }, [form.values.departmentId, form.values.structure]);

  // COMPONENT: Device deadlines
  const deadlines = useMemo(() => {
    return form.values.nextRevision.map((item, index) => {
      if (item.deleted) {
        return null;
      }

      const isHighlightedRevisionPlan =
        isRevisionPlanHighlightVisible &&
        highlightedRevisionPlanId !== undefined &&
        item.revisionPlanId === highlightedRevisionPlanId;

      return (
        <Box
          key={item.key}
          pt={24}
          pb={24}
          pl={isTablet ? 20 : 40}
          pr={isTablet ? 20 : 40}
          ml={isTablet ? 20 : 40}
          mr={24}
          mb={24}
          bg={isHighlightedRevisionPlan ? 'yellow.0' : 'gray.0'}
          sx={() => ({
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 80px' : '1fr 135px',
            alignItems: 'flex-end',
            alignContent: 'flex-end',
            gap: isMobile ? '24px' : '40px',
            alignSelf: 'stretch',
            flexWrap: 'wrap',
            borderRadius: '4px',
            border: isHighlightedRevisionPlan ? `1px solid ${theme.colors.yellow[5]}` : '1px solid transparent',
            boxShadow: isHighlightedRevisionPlan ? `0 0 0 2px ${theme.colors.yellow[2]}` : 'none',
            transition: 'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
          })}
        >
          <Stack spacing={24}>
            <Box
              data-testid="device-next-revision-primary-row"
              className={
                isMobile
                  ? 'grid grid-cols-1 gap-5'
                  : `grid grid-cols-[calc((100%-120px)/4*3+80px),calc((100%-120px)/4)] ${isTablet ? 'gap-5' : 'gap-10'}`
              }
            >
              <TextInput
                label="Úkon"
                placeholder="--"
                size="lg"
                disabled={readOnly.nextRevision}
                readOnly={readOnly.nextRevision}
                w="100%"
                name="objekto-nextRevision[].description"
                withAsterisk
                {...form.getInputProps(`nextRevision.${index}.description`)}
              />
              <DeviceTypeSelect
                label="Skupina"
                size="lg"
                disabled={readOnly.nextRevision}
                readOnly={readOnly.nextRevision}
                name="objekto-nextRevision[].deviceTypeId"
                {...form.getInputProps(`nextRevision.${index}.deviceTypeId`)}
              />
            </Box>
            <Stack spacing={isMobile ? 16 : 24}>
              <Stack spacing={8}>
                <DateInput
                  label="Dátum"
                  size="lg"
                  disabled={readOnly.nextRevision || item.neverExecuted}
                  readOnly={readOnly.nextRevision || item.neverExecuted}
                  value={form.getInputProps(`nextRevision.${index}.date`).value}
                  onChange={(val) => {
                    if (val && item.revisionPeriod) {
                      form.setFieldValue(
                        `nextRevision.${index}.nextDate`,
                        stringifyDate(addMonths(new Date(val), Number(item.revisionPeriod)))
                      );
                    }

                    form.getInputProps(`nextRevision.${index}.date`).onChange(val);
                  }}
                />
                <Checkbox
                  label="Neevidujeme dátum vykonania"
                  {...form.getInputProps(`nextRevision.${index}.neverExecuted`, { type: 'checkbox' })}
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFieldValue(`nextRevision.${index}.date`, '');
                    } else {
                      form.setFieldValue(`nextRevision.${index}.nextDate`, '');
                    }

                    form.getInputProps(`nextRevision.${index}.neverExecuted`, { type: 'checkbox' }).onChange(e);
                  }}
                  name="objekto-nextRevision[].neverExecuted"
                  disabled={readOnly.nextRevision}
                  readOnly={readOnly.nextRevision}
                />
              </Stack>

              <RevisionPeriodSelect
                label="Perióda"
                size="lg"
                placeholder="Vyberte periódu revízie"
                disabled={readOnly.nextRevision}
                readOnly={readOnly.nextRevision}
                value={form.getInputProps(`nextRevision.${index}.revisionPeriod`).value}
                onChange={(val) => {
                  if (form.values.nextRevision[index].date !== '' && val) {
                    form.setFieldValue(
                      `nextRevision.${index}.nextDate`,
                      stringifyDate(addMonths(new Date(item.date), Number(val)))
                    );
                  }

                  form.getInputProps(`nextRevision.${index}.revisionPeriod`).onChange(val);
                }}
                name="objekto-nextRevision[].revisionPeriod"
              />
              <DateInput
                label="Nasledujúci termín (nepovinné)"
                size="lg"
                disabled={!item.neverExecuted}
                readOnly={!item.neverExecuted}
                value={form.getInputProps(`nextRevision.${index}.nextDate`).value}
                onChange={(e) => {
                  if (item.date !== '' && item.revisionPeriod) {
                    noop();
                  } else {
                    form.getInputProps(`nextRevision.${index}.nextDate`).onChange(e);
                  }
                }}
              />

              <TextInput
                label="Externé ID"
                placeholder="---"
                size="lg"
                disabled
                readOnly
                name="objekto-nextRevision[].externalId"
                {...form.getInputProps(`nextRevision.${index}.externalId`)}
              />
            </Stack>
          </Stack>

          <Flex align="flex-end" maw={isMobile ? '100%' : 135}>
            <Box py={32}>
              <Button
                mt={27}
                type="button"
                variant="danger-secondary"
                leftIcon={
                  <IconTrashX
                    stroke="1.5"
                    height={24}
                    width={24}
                    color={readOnly.nextRevision ? theme.colors.red[2] : theme.colors.red[8]}
                  />
                }
                size="md"
                loading={loading}
                onClick={() => handleDeleteNextRevision(index)}
                disabled={readOnly.nextRevision}
                fullWidth={isMobile}
              >
                Zmazať
              </Button>
            </Box>
          </Flex>
        </Box>
      );
    });
  }, [
    form.values.nextRevision,
    highlightedRevisionPlanId,
    isRevisionPlanHighlightVisible,
    readOnly.nextRevision,
    theme,
    isMobile,
    isTablet,
    loading,
  ]);

  /**
   * Submit with validation.
   */
  function submitForm() {
    submit(form.values);
    form.resetDirty();
  }

  /**
   * Checks whether department was changed in edit mode.
   */
  function shouldConfirmDepartmentChange() {
    return (
      confirmDepartmentChange &&
      !isAddMode &&
      Boolean(initialValues.departmentId) &&
      form.values.departmentId !== initialValues.departmentId
    );
  }

  /**
   * Applies structure change and clears incompatible associations.
   */
  function applyStructureChange(structure: string) {
    form.setFieldValue('structure', structure);

    if (structure !== 'child') {
      form.setFieldValue('parentDeviceId', '');
    }

    if (structure !== 'parent') {
      form.setFieldValue('childDeviceIds', []);
    }
  }

  /**
   * Returns true if structure change should be confirmed due to association loss.
   */
  function shouldConfirmStructureChange(nextStructure: string) {
    if (nextStructure === form.values.structure) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return false;
    }

    if (form.values.structure === 'child' && Boolean(form.values.parentDeviceId)) {
      return true;
    }

    if (form.values.structure === 'parent' && (form.values.childDeviceIds?.length ?? 0) > 0) {
      return true;
    }

    return false;
  }

  /**
   * Handles structure change with confirmation when existing associations would be removed.
   */
  function handleStructureChange(nextStructure: string) {
    if (shouldConfirmStructureChange(nextStructure)) {
      confirm({
        title: 'Zmena štruktúry zariadenia',
        content:
          'Zmenou štruktúry zariadenia sa zrušia väzby medzi nadradeným a podradenými zariadeniami. Prajete si pokračovať?',
        onConfirm: () => {
          applyStructureChange(nextStructure);
        },
      });

      return;
    }

    applyStructureChange(nextStructure);
  }

  /**
   * Handles submit with validation and optional department change confirmation.
   */
  const sectionLayout = useEvidenceSections();
  const evidenceLayout = useDeviceEvidenceFields(
    form.values.departmentId,
    form.values.deviceSubtypeId,
    FEATURE_TOGGLE_DEVICE_SUBTYPE_EVIDENCE_FIELDS
  );
  const layoutLoading = sectionLayout.loading || evidenceLayout.loading;
  const layoutError =
    sectionLayout.error ||
    evidenceLayout.error ||
    (!sectionLayout.loading &&
      evidenceLayout.fields.some(
        (field) =>
          !sectionLayout.sections.some(
            (section) => section.module === 'devices' && section.sectionId === field.sectionId
          )
      ));

  /** Saves only after the applicable evidence layout has loaded. */
  const onSubmitImpl: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (layoutLoading || layoutError) return;
    const { hasErrors } = form.validate();

    if (!hasErrors) {
      if (shouldConfirmDepartmentChange()) {
        setDepartmentChangeModalOpened(true);
        return;
      }

      submitForm();
    } else {
      showNotification({
        title: 'Vo formulári sú chyby',
        message: (
          <Text>
            Prosím, skontrolujte všetky polia a opravte chyby.
            <br />
            Po odstránení chýb môžete formulár znovu odoslať.
          </Text>
        ),
        color: ERROR_NOTIFICATION_COLOR,
      });
    }
  };

  return (
    <>
      <FormWrapper
        onSubmit={onSubmitImpl}
        loading={loading || layoutLoading}
        contentLoading={layoutLoading}
        primaryButtonText={effectivePrimaryButtonText}
        primaryIcon={effectivePrimaryIcon}
        secondaryButtonText={effectiveSecondaryButtonText}
        closeConfirmTitle={effectiveCloseConfirmTitle}
        closeConfirmMessage={effectiveCloseConfirmMessage}
        onSecondaryClick={deviceFormReferrer ? () => navigate(deviceFormReferrer.returnTo) : undefined}
        hidePrimaryButton={hidePrimaryButton}
        hideSecondaryButton={hideSecondaryButton}
        hideFooter={hideFooter}
        skipSecondaryButtonConfirm={!form.isDirty()}
      >
        {layoutError && (
          <Toast
            type="fail"
            message="Sekcie alebo evidenčné polia sa nepodarilo načítať. Obnovte stránku pred uložením."
            withCloseButton={false}
          />
        )}
        <EvidenceSections
          module="devices"
          sections={sectionLayout.sections}
          fields={evidenceLayout.fields}
          builtinGroups={{
            basic_info: (
              <FormInputGroup groupTitle="Základné info">
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  <TextInput
                    label="Názov"
                    placeholder="Názov zariadenia"
                    size="lg"
                    name="objekto-deviceName"
                    withAsterisk
                    {...form.getInputProps('deviceName')}
                    disabled={readOnly.deviceName}
                    readOnly={readOnly.deviceName}
                  />
                  <DepartmentSelect
                    label={departmentText('Položka úrovne', 'Stredisko')}
                    size="lg"
                    placeholder={departmentText('Vyberte položku úrovne', 'Vyberte stredisko')}
                    showDiscarded={false}
                    groupByOrganization={true}
                    organizationId={departmentOrganizationId}
                    permissionSlug="manage-devices"
                    onReady={setDepartments}
                    name="objekto-departmentId"
                    withAsterisk
                    {...form.getInputProps('departmentId')}
                    readOnly={readOnly.departmentId || form.values.structure !== 'simple'}
                    disabled={readOnly.departmentId || form.values.structure !== 'simple'}
                    onChange={(value) => {
                      if (value) {
                        if (form.values.departmentId !== value) {
                          form.setFieldValue('responsiblePersonId', '');
                        }

                        form.setFieldValue('departmentId', value);
                      }
                    }}
                  />
                </SimpleGrid>
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  <TextInput
                    label="Výrobca"
                    placeholder="Výrobca zariadenia"
                    size="lg"
                    disabled={readOnly.manufacturer}
                    readOnly={readOnly.manufacturer}
                    name="objekto-manufacturer"
                    {...form.getInputProps('manufacturer')}
                  />
                  <TextInput
                    label="Výrobné číslo"
                    placeholder="Výrobné číslo zariadenia"
                    size="lg"
                    disabled={readOnly.serialNumber}
                    readOnly={readOnly.serialNumber}
                    name="objekto-serialNumber"
                    {...form.getInputProps('serialNumber')}
                  />
                </SimpleGrid>
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  <NumberInput
                    label="Rok výroby"
                    hideControls
                    size="lg"
                    disabled={readOnly.manufactured}
                    readOnly={readOnly.manufactured}
                    placeholder="Rok výroby zariadenia"
                    name="objekto-manufactured"
                    {...form.getInputProps('manufactured')}
                    precision={0}
                    step={1}
                  />
                  {FEATURE_TOGGLE_DEVICE_QR_CODES && showQrId && (
                    <DeviceQrIdInput
                      label="QR ID"
                      placeholder="QR ID zariadenia"
                      size="lg"
                      name="objekto-qrId"
                      deviceName={form.values.deviceName}
                      onQrIdScanned={(qrId) => form.setFieldValue('qrId', qrId)}
                      {...form.getInputProps('qrId')}
                      disabled={readOnly.qrId}
                      readOnly={readOnly.qrId}
                    />
                  )}
                </SimpleGrid>
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  <DeviceTypeSelect
                    label="Zariadenie"
                    size="lg"
                    placeholder="Vyberte zariadenie"
                    disabled={readOnly.deviceTypeId}
                    readOnly={readOnly.deviceTypeId}
                    name="objekto-deviceTypeId"
                    value={form.values.deviceTypeId}
                    withAsterisk
                    onChange={(value) => {
                      if (value) {
                        form.setFieldValue('deviceTypeId', value);
                        form.setFieldValue('deviceSubtypeId', '');
                      }
                    }}
                  />
                  {form.values.deviceTypeId !== '' && form.values.departmentId !== '' && (
                    <DeviceSubtypeSelect
                      label="Typ zariadenia"
                      size="lg"
                      parentDeviceTypeId={Number(form.values.deviceTypeId)}
                      departmentId={Number(form.values.departmentId)}
                      placeholder="Vyberte typ zariadenia"
                      disabled={readOnly.deviceTypeId}
                      readOnly={readOnly.deviceTypeId}
                      showDiscarded={false}
                      withAsterisk
                      name="objekto-deviceSubtypeId"
                      {...form.getInputProps('deviceSubtypeId')}
                    />
                  )}
                </SimpleGrid>
                {showRemoteSource && (
                  <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                    <TextInput
                      label="Externé ID"
                      size="lg"
                      placeholder="---"
                      disabled={Boolean(readOnly.externalId)}
                      readOnly={Boolean(readOnly.externalId)}
                      name="objekto-externalId"
                      rightSection={<P3Medium>{remoteSource?.name}</P3Medium>}
                      {...form.getInputProps('externalId')}
                    />
                  </SimpleGrid>
                )}
              </FormInputGroup>
            ),
            structure: FEATURE_TOGGLE_PARENT_DEVICE && (
              <FormInputGroup groupTitle="Štruktúra">
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  <Radio.Group
                    name="structure"
                    label="Určenie štruktúry zariadenia"
                    value={form.values.structure}
                    onChange={handleStructureChange}
                  >
                    <Group spacing={20} pt={8}>
                      <Radio
                        value="simple"
                        readOnly={readOnly.structure}
                        label={<span className="ml-[-4px]">Obyčajné zariadenie</span>}
                      />
                      <Radio
                        value="parent"
                        readOnly={readOnly.structure}
                        label={<span className="ml-[-4px]">Nadradené zariadenie</span>}
                      />
                      <Radio
                        value="child"
                        readOnly={readOnly.structure}
                        label={<span className="ml-[-4px]">Podradené zariadenie</span>}
                      />
                    </Group>
                  </Radio.Group>
                  <Box />
                  {form.values.structure === 'child' && (
                    <Box className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
                      <DeviceSelect
                        disabled={!form.values.departmentId}
                        departmentId={Number(form.values.departmentId)}
                        label="Nadradené zariadenie"
                        onlyParents
                        readOnly={readOnly.parentDeviceId}
                        {...form.getInputProps('parentDeviceId')}
                      />
                      <Button
                        component={LinkWithConfirm}
                        confirmTitle="Odchod z formulára"
                        confirmMessage="Po odchode z formulára sa vykonané zmeny neuložia automaticky. Uistite sa, že ste zmeny uložili. Chcete pokračovať?"
                        to={parentDeviceLink}
                        type="button"
                        variant="secondary"
                        leftIcon={<IconEye stroke="1.5" height={20} width={20} />}
                        disabled={!form.values.parentDeviceId}
                        bypassConfirm={!form.isDirty()}
                      >
                        Otvoriť
                      </Button>
                    </Box>
                  )}
                  {form.values.structure === 'parent' && (
                    <Box>
                      <Group spacing={8} align="center" mb={4}>
                        <P3Regular color="gray.6">Podradené zariadenia</P3Regular>
                        <QuestionMarkTooltip label="kliknutím na názov otvoríte detail zariadenia" />
                      </Group>
                      <Box className="grid gap-3">
                        {form.values.childDeviceIds?.map((childDeviceId, index) => (
                          <Group key={`${childDeviceId}-${index}`} align="center" position="left" spacing={12}>
                            <ChildDeviceNameText deviceId={childDeviceId} withLink confirmOnNavigate={form.isDirty()} />
                            {!readOnly.childDeviceIds && (
                              <Tooltip label="Odobrať podradené zariadenie" withArrow={false} openDelay={300}>
                                <ActionIcon variant="subtle" size="md" onClick={() => handleRemoveChildDevice(index)}>
                                  <IconTrashX stroke="1.5" height={20} width={20} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        ))}

                        {form.values.childDeviceIds?.length === 0 && (
                          <Toast
                            message="Zatiaľ nie sú žiadne podradené zariadenia. Pridajte nové zariadenie pomocou tlačidla 'Pridať'."
                            type="info"
                            withCloseButton={false}
                            size="s"
                          />
                        )}

                        {!readOnly.childDeviceIds && (
                          <Box className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
                            <DeviceSelect
                              mt={4}
                              value={newChildDeviceId}
                              placeholder="Pridať podradené zariadenie"
                              loadingPlaceholder="Pridať podradené zariadenie"
                              disabled={!form.values.departmentId}
                              departmentId={Number(form.values.departmentId)}
                              excludeParents
                              excludeDeviceIds={addChildDeviceExcludeDeviceIds}
                              onChange={(value) => setNewChildDeviceId(value ?? '')}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              leftIcon={<IconPlus stroke="1.5" height={20} width={20} />}
                              disabled={!newChildDeviceId || !form.values.departmentId}
                              onClick={() => {
                                if (!newChildDeviceId) {
                                  /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
                                  return;
                                }

                                if (!(form.values.childDeviceIds ?? []).includes(newChildDeviceId)) {
                                  form.setFieldValue('childDeviceIds', [
                                    ...(form.values.childDeviceIds ?? []),
                                    newChildDeviceId,
                                  ]);
                                }

                                setNewChildDeviceId('');
                              }}
                            >
                              Pridať
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </SimpleGrid>
              </FormInputGroup>
            ),
            location: (
              <FormInputGroup groupTitle="Umiestnenie">
                {showLegacyLocationFields && (
                  <>
                    <Group grow spacing={isTablet ? 20 : 40}>
                      <TextInput
                        label="Budova"
                        placeholder="Názov budovy"
                        size="lg"
                        disabled={readOnly.building}
                        readOnly={readOnly.building}
                        name="objekto-building"
                        {...form.getInputProps('building')}
                      />
                      <TextInput
                        label="Podlažie"
                        placeholder="Podlažie v budove"
                        size="lg"
                        disabled={readOnly.floor}
                        readOnly={readOnly.floor}
                        name="objekto-floor"
                        {...form.getInputProps('floor')}
                      />
                    </Group>
                    <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                      <TextInput
                        label="Miestnosť"
                        placeholder="Miestnosť v budove"
                        size="lg"
                        disabled={readOnly.room}
                        readOnly={readOnly.room}
                        name="objekto-room"
                        {...form.getInputProps('room')}
                      />
                    </SimpleGrid>
                  </>
                )}
                <Group grow spacing={isTablet ? 20 : 40}>
                  <NumberInput
                    precision={6}
                    min={-90}
                    max={90}
                    hideControls
                    decimalSeparator=","
                    label="Zemepisná šírka"
                    placeholder="48,590324"
                    size="lg"
                    disabled={readOnly.latitude}
                    readOnly={readOnly.latitude}
                    name="objekto-latitude"
                    {...form.getInputProps('latitude')}
                  />
                  <NumberInput
                    precision={6}
                    min={-180}
                    max={180}
                    hideControls
                    decimalSeparator=","
                    label="Zemepisná dĺžka"
                    placeholder="18,081704"
                    size="lg"
                    disabled={readOnly.longitude}
                    readOnly={readOnly.longitude}
                    name="objekto-longitude"
                    {...form.getInputProps('longitude')}
                  />
                </Group>
              </FormInputGroup>
            ),
            additional_info: (
              <FormInputGroup groupTitle="Doplňujúce info">
                <Group grow spacing={isTablet ? 20 : 40}>
                  <TextInput
                    label="Interné č. majetku"
                    placeholder="Interné číslo majetku"
                    size="lg"
                    disabled={readOnly.internalPossessionsNumber}
                    readOnly={readOnly.internalPossessionsNumber}
                    name="objekto-internalPossessionsNumber"
                    {...form.getInputProps('internalPossessionsNumber')}
                  />
                  <TextInput
                    label="Poznámka"
                    placeholder="Poznámka k int. číslu majetku"
                    size="lg"
                    disabled={readOnly.internalNote}
                    readOnly={readOnly.internalNote}
                    name="objekto-internalNote"
                    {...form.getInputProps('internalNote')}
                  />
                </Group>
              </FormInputGroup>
            ),
            responsible_person: (
              <FormInputGroup
                groupTitle="Zodpovedná osoba"
                infoBox={
                  form.isDirty('departmentId') && readOnly.responsiblePersonId ? (
                    <Toast
                      type="warning"
                      message="Skontrolujte zodpovednú osobu"
                      size="s"
                      opened={openedToast}
                      withCloseButton
                      onClose={() => setOpenedToast(false)}
                    />
                  ) : undefined
                }
              >
                <Group grow spacing={isTablet ? 20 : 40}>
                  {form.values.departmentId ? (
                    <DeviceResponsiblePersonSelect
                      clearable
                      label="Zodpovedná osoba"
                      size="lg"
                      placeholder="Vyberte zodpovednú osobu"
                      departmentId={Number(form.values.departmentId)}
                      disabled={readOnly.responsiblePersonNote}
                      readOnly={readOnly.responsiblePersonNote}
                      name="objekto-responsiblePersonId"
                      {...form.getInputProps('responsiblePersonId')}
                    />
                  ) : (
                    <Stack py={16}>
                      <P2Regular color="gray.8">Najskôr vyberte organizáciu</P2Regular>
                    </Stack>
                  )}
                  <TextInput
                    label="Poznámka"
                    placeholder="Poznámka k zodpovednej osobe"
                    size="lg"
                    disabled={readOnly.responsiblePersonNote}
                    readOnly={readOnly.responsiblePersonNote}
                    name="objekto-responsiblePersonNote"
                    {...form.getInputProps('responsiblePersonNote')}
                  />
                </Group>
              </FormInputGroup>
            ),
          }}
          renderFields={(fields) => (
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              {fields.map((field) => (
                <Box key={field.evidenceFieldId}>
                  <DeviceEvidenceField form={form} {...field} />
                </Box>
              ))}
            </SimpleGrid>
          )}
        />

        <Divider mt={40} color="gray.1" />

        {form.values.parentDeviceId && (
          <>
            <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
              <Group spacing={8} align="center">
                <IconArrowUpRight stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
                <H3Medium color="gray.7">Nadradené termíny</H3Medium>
              </Group>
            </Flex>

            <ParentDeviceRevisionPlans parentDeviceId={form.values.parentDeviceId} />

            <Divider mt={40} color="gray.1" />
          </>
        )}

        {/* START SECTION: Revision deadlines */}
        <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
          <Group spacing={8} align="center">
            <IconCalendar stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
            <H3Medium color="gray.7">Sledovanie termínov</H3Medium>
          </Group>
        </Flex>

        <Box sx={{ transform: 'translateY(-80px)' }} ref={deadlinesAnchorRef} />
        {deadlines}

        <Group position="left" mt="md" ml={isTablet ? 20 : 40}>
          <Button
            onClick={() =>
              form.insertListItem('nextRevision', {
                description: '',
                date: '',
                revisionPeriod: '',
                key: randomId(),
                deleted: false,
                neverExecuted: false,
                deviceTypeId: form.values.deviceTypeId,
              })
            }
            leftIcon={<IconPlus stroke="1.5" height={24} width={24} />}
            size="md"
            variant="subtle"
            disabled={readOnly.nextRevision}
            fullWidth={isMobile}
          >
            Pridať ďalší termín
          </Button>
        </Group>
        {/* END SECTION: Revision deadlines */}

        {form.values.parentDeviceId && !readOnly.faults && (
          <>
            <Divider mt={40} color="gray.1" />
            <Flex
              align={isMobile ? 'flex-start' : 'center'}
              direction={isMobile ? 'column' : 'row'}
              ml={isTablet ? 20 : 40}
              mt={40}
              mb={16}
              gap={isMobile ? 12 : 24}
            >
              <Group spacing={8} align="center">
                <IconArrowUpRight stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
                <H3Medium color="gray.7">Nadradené nedostatky</H3Medium>
              </Group>
              <LinkWithConfirm
                to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId: form.values.parentDeviceId })}
                confirmMessage="Po odchode z formulára sa vykonané zmeny neuložia automaticky. Uistite sa, že ste zmeny uložili. Chcete pokračovať?"
                confirmTitle="Odchod z formulára"
                bypassConfirm={!form.isDirty()}
              >
                <P2Medium>Zobraziť zoznam nedostatkov nadradeného zariadenia</P2Medium>
              </LinkWithConfirm>
            </Flex>

            <ParentDeviceFaults parentDeviceId={form.values.parentDeviceId} />
          </>
        )}

        {/* START SECTION: Device faults */}
        {!readOnly.faults && (
          <>
            <Divider mt={40} color="gray.1" />
            <Flex
              align={isMobile ? 'flex-start' : 'center'}
              direction={isMobile ? 'column' : 'row'}
              ml={isTablet ? 20 : 40}
              mt={40}
              mb={16}
              gap={isMobile ? 12 : 24}
            >
              <Group spacing={8} align="center">
                <IconAlertTriangle stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
                <H3Medium color="gray.7">Zoznam nedostatkov</H3Medium>
              </Group>
              <LinkWithConfirm
                to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId: deviceId! })}
                confirmMessage="Po odchode z formulára sa vykonané zmeny neuložia automaticky. Uistite sa, že ste zmeny uložili. Chcete pokračovať?"
                confirmTitle="Odchod z formulára"
                bypassConfirm={!form.isDirty()}
              >
                <P2Medium>Zobraziť zoznam všetkých nedostatkov</P2Medium>
              </LinkWithConfirm>
            </Flex>

            {deviceFaultsFields}

            <Group position="left" mt="md" ml={isTablet ? 20 : 40}>
              <Button
                onClick={() =>
                  form.insertListItem('faults', {
                    faultName: '',
                    faultType: 'Bez nedostatku',
                    description: '',
                    key: randomId(),
                    deleted: false,
                  })
                }
                leftIcon={<IconPlus stroke="1.5" height={24} width={24} />}
                size="md"
                variant="subtle"
                fullWidth={isMobile}
              >
                Pridať ďalší nedostatok
              </Button>
            </Group>
          </>
        )}
        {/* END SECTION: Device faults */}

        {form.values.parentDeviceId && (
          <>
            <Divider mt={40} color="gray.1" />
            <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
              <Group spacing={8} align="center">
                <IconArrowUpRight stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
                <H3Medium color="gray.7">Nadradené súbory</H3Medium>
              </Group>
            </Flex>
            <Box px={isTablet ? 20 : 40}>
              <ParentDeviceFiles parentDeviceId={form.values.parentDeviceId} />
            </Box>
          </>
        )}

        {/* START SECTION: Device files */}
        {hasVisibleFileTabs && <Divider mt={40} color="gray.1" />}
        {hasVisibleFileTabs && (
          <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
            <Group spacing={8} align="center">
              <IconFile stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
              <H3Medium color="gray.7">Súbory</H3Medium>
            </Group>
          </Flex>
        )}
        <Box pl={isTablet ? 20 : 40} pr={isTablet ? 20 : 40} pb={40}>
          <DeviceFiles
            context="form"
            deviceId={deviceId ? Number(deviceId) : 0}
            deviceName={form.values.deviceName}
            departmentId={form.values.departmentId ? Number(form.values.departmentId) : undefined}
            form={form}
            files={form.values.files}
            readonly={readOnly.files}
            persistActiveTab={false}
            onVisibleTabsChange={(count) => setHasVisibleFileTabs(count > 0)}
            initialTab={isMobile ? 'photos' : 'project-documentation'}
          />
        </Box>
        {/* END SECTION: Device files */}
      </FormWrapper>

      <ConfirmDeviceDepartmentChangeModal
        opened={departmentChangeModalOpened}
        loading={loading}
        originalDepartmentId={initialValues.departmentId}
        newDepartmentId={form.values.departmentId}
        onClose={() => setDepartmentChangeModalOpened(false)}
        onConfirm={() => {
          setDepartmentChangeModalOpened(false);
          submitForm();
        }}
      />
    </>
  );
}
